import { useLocation, useNavigate, Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import "./LegalApp.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import axios from 'axios';

/** Same TableSection pattern as InvestigateFIR.js :contentReference[oaicite:1]{index=1} */
const TableSection = ({ children }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (containerRef.current) containerRef.current.scrollLeft = 0;
        }, 100);
        return () => clearTimeout(timeout);
    }, []);

    return (
        <div className="table-section" ref={containerRef}>
            {children}
        </div>
    );
};

const NumberedList = ({ items }) => {
    const arr = Array.isArray(items) ? items : [];
    if (arr.length === 0) return <div>-</div>;

    // Removes a leading index like:
    // "1. text", "2) text", "03. text", with optional whitespace
    const stripLeadingIndex = (value) => {
        const s = String(value ?? "");
        return s.replace(/^\s*\d+\s*[\.\)]\s*/, "");
    };

    return (
        <div>
            {arr.map((x, i) => {
                const cleaned = stripLeadingIndex(x);

                return (
                    <div
                        key={i}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px", // space between index and paragraph text
                            marginBottom: "14px", // gap between paragraphs
                        }}
                    >
                        <div
                            style={{
                                width: "28px", // fixed width so text aligns like paragraphs
                                flexShrink: 0,
                                textAlign: "right",
                                fontWeight: 600,
                                lineHeight: 1.5,
                            }}
                        >
                            {i + 1}.
                        </div>

                        <div style={{ flex: 1, lineHeight: 1.5 }}>
                            {cleaned}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


const FIRRegistrationFacts = ({ text, translatedText, onRefresh }) => {
    const [copied, setCopied] = useState(false);
    const [showCopyError, setShowCopyError] = useState(null);

    // Prefer translatedText if present, else fall back to text
    const displayText =
        (translatedText && translatedText.trim().length > 0)
            ? translatedText
            : (text && text.trim().length > 0 ? text : null);

    const handleCopy = async () => {
        if (!displayText) return;

        try {
            await navigator.clipboard.writeText(displayText);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            console.error("Copy failed", err);
            setShowCopyError("Copy failed. Please try again.");
        }
    };

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>Facts leading to registration of FIR</span>

                                {!copied ? (
                                    <span
                                        onClick={handleCopy}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            cursor: displayText ? "pointer" : "not-allowed",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                            opacity: displayText ? 1 : 0.5,
                                        }}
                                        title={displayText ? "Copy" : "Nothing to copy yet"}
                                    >
                                        {/* Copy icon */}
                                        <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                        </svg>
                                        Copy
                                    </span>
                                ) : (
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                        }}
                                        title="Copied"
                                    >
                                        {/* Tick icon */}
                                        <svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 12-12-1.5-1.5L9 16.2z" />
                                        </svg>
                                        Copied
                                    </span>
                                )}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td
                                style={{
                                    textAlign: "justify",
                                    verticalAlign: "top",
                                    paddingTop: "15px",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {displayText ? (
                                    displayText.split("\n").map((line, idx) => (
                                        <span key={idx}>
                                            {line}
                                            <br />
                                        </span>
                                    ))
                                ) : (
                                    <span>
                                        AI assistant is processing this section. Click{" "}
                                        <span
                                            onClick={onRefresh}
                                            style={{
                                                color: "#007bff",
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                                fontWeight: "bold",
                                            }}
                                            onMouseOver={(e) => (e.currentTarget.style.color = "#0056b3")}
                                            onMouseOut={(e) => (e.currentTarget.style.color = "#007bff")}
                                        >
                                            Refresh
                                        </span>{" "}
                                        in about 30 minutes to view results.
                                    </span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {showCopyError && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h2>Error</h2>
                            <div className="error-message" style={{ marginBottom: "15px", fontSize: "16px", color: "#333" }}>
                                <strong>{showCopyError}</strong>
                            </div>
                            <button
                                className="error-ok-button"
                                onClick={() => setShowCopyError(null)}
                                style={{
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    color: "white",
                                    backgroundColor: "#007bff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </TableSection>
    );
};


const InvestigationConducted = ({
    investigationConductedList,
    translatedInvestigationConductedList,
    onRefresh
}) => {
    const [copied, setCopied] = useState(false);
    const [showCopyError, setShowCopyError] = useState(null);

    const stripLeadingIndex = (value) => {
        const s = String(value ?? "");
        return s.replace(/^\s*\d+\s*[\.\)]\s*/, "");
    };

    const origArr = Array.isArray(investigationConductedList)
        ? investigationConductedList
        : [];

    const transArr = Array.isArray(translatedInvestigationConductedList)
        ? translatedInvestigationConductedList
        : [];

    // Build what we actually display:
    // - show translated items first
    // - if translated list is shorter, append remaining originals with prefix
    const displayArr = (() => {
        const hasTranslated = transArr.length > 0;

        if (hasTranslated) {
            const remainingOrig = origArr.slice(transArr.length);
            const remainingWithPrefix = remainingOrig.map((x) => {
                const cleaned = stripLeadingIndex(x).trim();
                return `Translation in progress: ${cleaned}`;
            });

            return [...transArr, ...remainingWithPrefix];
        }

        // No translations yet: fall back to originals (if any)
        if (origArr.length > 0) return origArr;

        return [];
    })();

    const buildCopyText = () => {
        if (displayArr.length === 0) return "";
        return displayArr
            .map((x, i) => `${i + 1}. ${stripLeadingIndex(x).trim()}`)
            .join("\n");
    };

    const handleCopy = async () => {
        const textToCopy = buildCopyText();
        if (!textToCopy) return;

        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            console.error("Copy failed", err);
            setShowCopyError("Copy failed. Please try again.");
        }
    };

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>Investigation Conducted</span>

                                {!copied ? (
                                    <span
                                        onClick={handleCopy}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            cursor: displayArr.length > 0 ? "pointer" : "not-allowed",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                            opacity: displayArr.length > 0 ? 1 : 0.5,
                                        }}
                                        title={displayArr.length > 0 ? "Copy" : "Nothing to copy yet"}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="16"
                                            width="16"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                        </svg>
                                        Copy
                                    </span>
                                ) : (
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                        }}
                                        title="Copied"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="18"
                                            width="18"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 12-12-1.5-1.5L9 16.2z" />
                                        </svg>
                                        Copied
                                    </span>
                                )}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td
                                style={{
                                    textAlign: "justify",
                                    verticalAlign: "top",
                                    paddingTop: "15px",
                                }}
                            >
                                {displayArr.length > 0 ? (
                                    <NumberedList items={displayArr} />
                                ) : (
                                    <span>
                                        AI assistant is processing this section. Click{" "}
                                        <span
                                            onClick={onRefresh}
                                            style={{
                                                color: "#007bff",
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                                fontWeight: "bold",
                                            }}
                                            onMouseOver={(e) => (e.currentTarget.style.color = "#0056b3")}
                                            onMouseOut={(e) => (e.currentTarget.style.color = "#007bff")}
                                        >
                                            Refresh
                                        </span>{" "}
                                        in about 30 minutes to view results.
                                    </span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {showCopyError && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h2>Error</h2>
                            <div
                                className="error-message"
                                style={{ marginBottom: "15px", fontSize: "16px", color: "#333" }}
                            >
                                <strong>{showCopyError}</strong>
                            </div>
                            <button
                                className="error-ok-button"
                                onClick={() => setShowCopyError(null)}
                                style={{
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    color: "white",
                                    backgroundColor: "#007bff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </TableSection>
    );
};


const InvestigationConclusion = ({ text, translatedText, onRefresh }) => {
    const [copied, setCopied] = useState(false);
    const [showCopyError, setShowCopyError] = useState(null);

    // Prefer translatedText if present, else fall back to text
    const displayText =
        (translatedText && translatedText.trim().length > 0)
            ? translatedText
            : (text && text.trim().length > 0 ? text : null);

    const handleCopy = async () => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(displayText);
            setCopied(true);

            // Show tick for 3 seconds, then revert back to Copy
            window.setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            console.error("Copy failed", err);
            setShowCopyError("Copy failed. Please try again.");
        }
    };

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>Conclusion</span>

                                {/* Copy / Copied */}
                                {!copied ? (
                                    <span
                                        onClick={handleCopy}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                        }}
                                        title="Copy"
                                    >
                                        {/* Copy icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="16"
                                            width="16"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                                        </svg>
                                        Copy
                                    </span>
                                ) : (
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "black",
                                            userSelect: "none",
                                        }}
                                        title="Copied"
                                    >
                                        {/* Tick icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="18"
                                            width="18"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 12-12-1.5-1.5L9 16.2z" />
                                        </svg>
                                        Copied
                                    </span>
                                )}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td
                                style={{
                                    textAlign: "justify",
                                    verticalAlign: "top",
                                    paddingTop: "15px",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {text ? (
                                    displayText.split("\n").map((line, idx) => (
                                        <span key={idx}>
                                            {line}
                                            <br />
                                        </span>
                                    ))
                                ) : (
                                    <span>
                                        AI assistant is processing this section. Click{" "}
                                        <span
                                            onClick={onRefresh}
                                            style={{
                                                color: "#007bff",
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                                fontWeight: "bold",
                                            }}
                                            onMouseOver={(e) => (e.currentTarget.style.color = "#0056b3")}
                                            onMouseOut={(e) => (e.currentTarget.style.color = "#007bff")}
                                        >
                                            Refresh
                                        </span>{" "}
                                        in about 30 minutes to view results.
                                    </span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {showCopyError && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h2>Error</h2>
                            <div
                                className="error-message"
                                style={{ marginBottom: "15px", fontSize: "16px", color: "#333" }}
                            >
                                <strong>{showCopyError}</strong>
                            </div>
                            <button
                                className="error-ok-button"
                                onClick={() => setShowCopyError(null)}
                                style={{
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    color: "white",
                                    backgroundColor: "#007bff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </TableSection>
    );
};

/* ---------------- Section 1: Charge Sheet (1x1) ---------------- */
const ChargeSheetTable = ({ text }) => {
    const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
    const [showCopyError, setShowCopyError] = useState(null);
    const handleCopy = async () => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            setShowCopyConfirmation(true);
        } catch (err) {
            console.error("Copy failed", err);
            setShowCopyError("Copy failed. Please try again.");
        }
    };

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>Charge Sheet generated from Case Diary</span>

                                {/* Copy action */}
                                <span
                                    onClick={handleCopy}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: "black",
                                    }}
                                    title="Copy charge sheet"
                                >
                                    {/* Copy icon */}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="16"
                                        width="16"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 
                    1.1.9 2 2 2h11c1.1 0 2-.9 
                    2-2V7c0-1.1-.9-2-2-2zm0 
                    16H8V7h11v14z" />
                                    </svg>
                                    Copy
                                </span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td
                                style={{
                                    textAlign: "justify",
                                    verticalAlign: "top",
                                    paddingTop: "15px",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {text ? (
                                    text.split("\n").map((line, idx) => (
                                        <span key={idx}>
                                            {line}
                                            <br />
                                        </span>
                                    ))
                                ) : (
                                    "-"
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {showCopyConfirmation && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <p>Charge Sheet copied to clipboard.</p>
                            <div className="popup-buttons">
                                <button
                                    className="submit-popup-button"
                                    onClick={() => setShowCopyConfirmation(false)}
                                >OK</button>
                            </div>
                        </div>
                    </div>
                )}

                {showCopyError && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h2>Error</h2>
                            <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                                <strong>{showCopyError}</strong>
                            </div>
                            <button
                                className="error-ok-button"
                                onClick={() => setShowCopyError(null)}
                                style={{ padding: '10px 20px', fontSize: '14px', color: 'white', backgroundColor: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </TableSection>
    );
};

/* ---------------- Section 2: Narrative (1x2) ---------------- */
const NarrativeTable = ({ narrative, narrativeGaps }) => {
    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th>Narrative</th>
                            <th>Gaps</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px", width: "55%" }}>
                                {narrative ? (
                                    narrative.split("\n").map((line, idx) => (
                                        <span key={idx}>
                                            {line}
                                            <br />
                                        </span>
                                    ))
                                ) : (
                                    "-"
                                )}
                            </td>
                            <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                <NumberedList items={narrativeGaps} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ---------------- Section 3: Accused (nx3) ---------------- */
const AccusedTableCS = ({ roleOfAccused, roleOfAccusedGaps }) => {
    const roles = roleOfAccused && typeof roleOfAccused === "object" ? roleOfAccused : {};
    const gapsMap =
        roleOfAccusedGaps && typeof roleOfAccusedGaps === "object" ? roleOfAccusedGaps : {};

    const rows = Object.entries(roles); // [ [name, role], ... ]

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th style={{ width: "20%" }}>Name</th>
                            <th style={{ width: "35%" }}>Role</th>
                            <th>Gaps</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={3}>-</td>
                            </tr>
                        ) : (
                            rows.map(([name, role], idx) => (
                                <tr key={`${name}-${idx}`}>
                                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>{name || "-"}</td>
                                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                        {role || "-"}
                                    </td>
                                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                        {gapsMap?.[name] || "-"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ---------------- Section 4: Offenses (nx2) ---------------- */
const OffensesTableCS = ({ offensesAndSections }) => {
    const m =
        offensesAndSections && typeof offensesAndSections === "object"
            ? offensesAndSections
            : {};

    const rows = Object.entries(m); // [ [accusedName, [offenses...]], ... ]

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th style={{ width: "25%" }}>Accused</th>
                            <th>Offenses</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={2}>-</td>
                            </tr>
                        ) : (
                            rows.map(([name, offenses], idx) => (
                                <tr key={`${name}-${idx}`}>
                                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>{name || "-"}</td>
                                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                        <NumberedList items={offenses} />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ---------------- Section 5: Witnesses (nx3) ---------------- */
const WitnessesTableCS = ({ witnesses, witnessesGaps }) => {
    const w =
        witnesses && typeof witnesses === "object"
            ? witnesses
            : {}; // map[accusedName]map[witnessName]offense

    const g =
        witnessesGaps && typeof witnessesGaps === "object"
            ? witnessesGaps
            : {}; // map[accusedName]map[witnessName]gaps(string)

    // Build grouped structure:
    // accusedGroups = [ { accusedName, items: [{ witnessName, offense, gaps }] } ]
    const accusedGroups = Object.entries(w).map(([accusedName, witnessMap]) => {
        const wm = witnessMap && typeof witnessMap === "object" ? witnessMap : {};

        const items = Object.entries(wm).map(([witnessName, offense]) => ({
            witnessName,
            offense,
            gaps: g?.[accusedName]?.[witnessName],
        }));

        return { accusedName, items };
    });

    // Total witness rows count
    const totalRows = accusedGroups.reduce((sum, grp) => sum + (grp.items?.length || 0), 0);

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th style={{ width: "20%" }}>Accused</th>
                            <th>Witnesses</th>
                            <th style={{ width: "30%" }}>Gaps</th>
                        </tr>
                    </thead>

                    <tbody>
                        {totalRows === 0 ? (
                            <tr>
                                <td colSpan={3}>-</td>
                            </tr>
                        ) : (
                            accusedGroups.map((grp, grpIdx) => {
                                const rowspan = grp.items.length || 1;

                                // If an accused exists but has no witnesses, show one row with "-"
                                if (!grp.items || grp.items.length === 0) {
                                    return (
                                        <tr key={`${grp.accusedName || "unknown"}-${grpIdx}`}>
                                            <td style={{ verticalAlign: "top", paddingTop: "15px" }}>
                                                {grp.accusedName || "-"}
                                            </td>
                                            <td style={{ verticalAlign: "top", paddingTop: "15px" }}>-</td>
                                            <td style={{ verticalAlign: "top", paddingTop: "15px" }}>-</td>
                                        </tr>
                                    );
                                }

                                return grp.items.map((item, itemIdx) => (
                                    <tr key={`${grp.accusedName}-${item.witnessName}-${grpIdx}-${itemIdx}`}>
                                        {/* Render accused cell ONLY for the first witness row, and rowspan it */}
                                        {itemIdx === 0 && (
                                            <td
                                                rowSpan={rowspan}
                                                style={{ verticalAlign: "top", paddingTop: "15px" }}
                                            >
                                                {grp.accusedName || "-"}
                                            </td>
                                        )}

                                        <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                            <div>
                                                <strong>{item.witnessName || "-"}</strong>
                                            </div>
                                            <div style={{ color: "grey", marginTop: "6px", fontSize: "15px" }}>
                                                {item.offense || "-"}
                                            </div>
                                        </td>

                                        <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                            {item.gaps || "-"}
                                        </td>
                                    </tr>
                                ));
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ---------------- Section 6: Evidence (nx3) ---------------- */
const EvidenceTableCS = ({ evidences, evidencesGaps }) => {
    const e = evidences && typeof evidences === "object" ? evidences : {}; // map[accusedName][]evidences
    const g =
        evidencesGaps && typeof evidencesGaps === "object"
            ? evidencesGaps
            : {}; // map[accusedName][]gaps

    const accusedNames = Array.from(
        new Set([...Object.keys(e), ...Object.keys(g)])
    );

    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th style={{ width: "18%" }}>Accused</th>
                            <th>Evidence</th>
                            <th>Gaps</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accusedNames.length === 0 ? (
                            <tr>
                                <td colSpan={3}>-</td>
                            </tr>
                        ) : (
                            accusedNames.map((name, idx) => (
                                <tr key={`${name}-${idx}`}>
                                    <td style={{ verticalAlign: "top", paddingTop: "15px" }}>{name || "-"}</td>
                                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                        <NumberedList items={e?.[name]} />
                                    </td>
                                    <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                        <NumberedList items={g?.[name]} />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ---------------- Section 7: Actions Taken (nx1) ---------------- */
const ActionsTakenTableCS = ({ actionsTaken }) => {
    return (
        <TableSection>
            <div className="fir-table-container">
                <table className="fir-table">
                    <thead>
                        <tr>
                            <th>Actions Taken During Investigation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: "justify", verticalAlign: "top", paddingTop: "15px" }}>
                                <NumberedList items={actionsTaken} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </TableSection>
    );
};

/* ======================= Main Page ======================= */
const ChargeSheetView = ({ API_URL }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isDownloading, setIsDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const [initialFir] = useState(location.state?.fir ?? null);
    const setFullFIR = location.state?.setFullFIR;
    const [latestFir, setLatestFir] = useState(initialFir);

    const caseDiary = latestFir?.caseDiary ?? {};
    const [viewCaseDiary, setViewCaseDiary] = useState(false);

    const handlePrint = () => {
        // Add a style sheet to enforce minimum margins
        const style = document.createElement('style');
        style.innerHTML = `
    @page {
      margin-top: 5cm;
      margin-bottom: 1cm;
      margin-left: 1cm;
      margin-right: 1cm;
      scale: 0.9;
    }`;

        document.head.appendChild(style);
        window.print();
        document.head.removeChild(style);
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return "";

        const date = new Date(isoString);
        if (isNaN(date.getTime())) return "";

        const pad = (n) => String(n).padStart(2, "0");

        const day = pad(date.getUTCDate());
        const month = pad(date.getUTCMonth() + 1);
        const year = date.getUTCFullYear();
        const hours = pad(date.getUTCHours());
        const minutes = pad(date.getUTCMinutes());

        return `${day}/${month}/${year} ${hours}:${minutes} Hrs`;
    };

    const handleDownloadClick = async (id) => {
        setIsDownloading(true);

        try {
            const jwt = localStorage.getItem("jwtToken");

            const response = await axios.get(`${API_URL}/download/${id}`, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
                responseType: "blob",
            });

            // Use server-provided content type (fallback to octet-stream)
            const contentType =
                response.headers?.["content-type"] || "application/octet-stream";

            // Try to get filename from Content-Disposition
            const contentDisposition = response.headers?.["content-disposition"] || "";
            let filename = `FIR_${latestFir.id}`;

            // Supports: attachment; filename="abc.pdf"
            const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;"\n"]+)/i);
            if (match && match[1]) {
                filename = decodeURIComponent(match[1].replace(/"/g, "").trim());
            } else {
                // Fallback: add extension based on content type if you want
                // (Optional) if pdf and no filename returned
                if (contentType.includes("pdf")) filename += ".pdf";
                else if (contentType.includes("wordprocessingml")) filename += ".docx";
                else filename += ""; // leave as-is
            }

            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading FIR:", error);
            setErrorMessage(
                "An error occurred while downloading the FIR. Please try again later."
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const goToLogin = () => navigate("/login", { replace: true });
    const [pendingMessage, setPendingMessage] = useState(null);

    const getFIR = async () => {
        try {
            setIsLoading(true);

            const jwtToken = localStorage.getItem("jwtToken");
            if (!jwtToken) {
                setErrorMessage("Session Expired. Please log in again.");
                goToLogin();
                return null;
            }

            const response = await axios.get(`${API_URL}/fir/${latestFir.id}`, {
                headers: { Authorization: `Bearer ${jwtToken}` },
            });

            if (response.status === 200) {
                setLatestFir(response.data);
                localStorage.setItem(response.data.id, response.data);
                setPendingMessage("Latest record uploaded successfully. If any section is still processing or translation is incomplete, please check back after 30 minutes.");
                return;
            } else {
                setErrorMessage("Error fetching case record. Please try again.");
                return;
            }
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401) {
                setErrorMessage("Session Expired. Please log in again.");
                goToLogin();
                return;
            }
            console.error("Error fetching FIR:", error);
            setErrorMessage("Error processing request. Please try after sometime.");
            return;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fir-page-container">
            <Navbar />
            <main style={{ flex: 1 }}>
                <br />
                <br />

                <div className="info-section" style={{ marginTop: "35px" }}>
                    <center>
                        <b>Charge Sheet</b>
                        <br />
                        <span style={{ fontSize: "14px", fontWeight: "600" }}>
                            (Generated from Case Diary)
                        </span>
                        <br />
                        <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                            AI-generated. Verify the information before taking any action.
                        </span>
                    </center>
                </div>

                <div className="info-section">
                    <center>
                        <div style={{ color: '#c62828', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                            You are using the free version. For a more detailed analysis, Monthly and Annual paid subscriptions are available for Police Stations and District Police. Contact 9582822225 for pricing details.
                        </div>
                    </center>
                </div>

                {latestFir.caseDiary && latestFir.caseDiary.bucketFileName && (
                    <div className="info-section">
                        <center>
                            <button
                                className="submit-button"
                                style={{
                                    marginLeft: "auto",
                                    backgroundColor: "#FFFFFF",
                                    color: "#094268",
                                    fontWeight: "bold",
                                    border: "1px solid #094268",
                                }}
                                disabled={isDownloading}
                                onClick={() => handleDownloadClick(latestFir.caseDiary?.bucketFileName)}
                            >
                                {isDownloading ? (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: "50%",
                                                border: "2px solid currentColor",
                                                borderTopColor: "transparent",
                                                display: "inline-block",
                                                animation: "spin 0.8s linear infinite",
                                            }}
                                        />
                                        Downloading...
                                    </span>
                                ) : (
                                    "Download Case Diary"
                                )}
                            </button>
                        </center>

                        {/* Put this once globally (eg in App.css) */}
                        <style>
                            {`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}
                        </style>
                    </div>
                )}


                <div className="fir-page">

                    {caseDiary?.bucketFileName ? (
                        <>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                                <button
                                    type="button"
                                    onClick={getFIR}
                                    disabled={isLoading}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        padding: 0,
                                        color: "#094268",
                                        fontSize: "16px",
                                        lineHeight: "22px",
                                        fontWeight: "800",
                                        textDecoration: "underline",
                                        cursor: isLoading ? "not-allowed" : "pointer",
                                        opacity: isLoading ? 0.6 : 1,
                                    }}
                                >
                                    Refresh
                                </button>
                            </div>
                            <FIRRegistrationFacts text={caseDiary?.caseRegistrationFacts} translatedText={caseDiary?.caseRegistrationFactsTranslated} onRefresh={getFIR} />
                            <InvestigationConducted
                                investigationConductedList={caseDiary?.investigationConducted}
                                translatedInvestigationConductedList={caseDiary?.investigationConductedTranslated}
                                onRefresh={getFIR}
                            />
                            <InvestigationConclusion text={caseDiary?.conclusion} translatedText={caseDiary?.conclusionTranslated} onRefresh={getFIR} />
                        </>
                    ) : (
                        <div style={{ marginTop: '50px', marginBottom: '25px', alignContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                            <h4>Upload the Case Diary to generate the Charge Sheet.</h4>
                        </div>
                    )}


                    {/* <div className="info-section">
                        <p>
                            <b>Case Narrative</b>
                        </p>
                    </div>
                    <NarrativeTable
                        narrative={caseDiary?.narrative}
                        narrativeGaps={caseDiary?.narrativeGaps}
                    />
                    
                    <div className="info-section">
                        <p>
                            <b>Accused</b>
                        </p>
                    </div>
                    <AccusedTableCS
                        roleOfAccused={caseDiary?.roleOfAccused}
                        roleOfAccusedGaps={caseDiary?.roleOfAccusedGaps}
                    />

                    <div className="info-section">
                        <p>
                            <b>Sections & Offenses Applied</b>
                        </p>
                    </div>
                    <OffensesTableCS offensesAndSections={caseDiary?.offensesAndSections} />

                    <div className="info-section">
                        <p>
                            <b>Witnesses</b>
                        </p>
                    </div>
                    <WitnessesTableCS
                        witnesses={caseDiary?.witnesses}
                        witnessesGaps={caseDiary?.witnessesGaps}
                    />

                    <div className="info-section">
                        <p>
                            <b>Evidence</b>
                        </p>
                    </div>
                    <EvidenceTableCS
                        evidences={caseDiary?.evidences}
                        evidencesGaps={caseDiary?.evidencesGaps}
                    />

                    <div className="info-section">
                        <p>
                            <b>Actions Taken</b>
                        </p>
                    </div>
                    <ActionsTakenTableCS actionsTaken={caseDiary?.actionsTaken} /> */}

                    {/* Back link + right side buttons */}
                    <div
                        className="info-section"
                        style={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "-30px",
                            marginBottom: "50px",
                            padding: "0 20px",
                            width: "100%",
                        }}
                    >
                        {/* Left: Charge Sheets List */}
                        <div className="info-section">
                            <Link
                                to="/fir/dashboard"
                                state={{ fullFIR: latestFir }}
                                style={{
                                    color: "#007AFF",
                                    textDecoration: "none",
                                    fontWeight: 600,
                                    display: "inline-flex",
                                    alignItems: "left",
                                    gap: "6px",
                                    fontSize: "16px",
                                }}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        fontSize: "24px",
                                        fontWeight: 800,
                                        lineHeight: "20px",
                                        marginRight: "2px",
                                    }}
                                >
                                    ←
                                </span>
                                <span>Back</span>
                            </Link>
                        </div>

                        {/* Right: Buttons */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <button
                                onClick={() => navigate("/fir/casediary/upload", { state: { fir: latestFir } })}
                                className='print-button'
                                style={{
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #ccc",
                                    borderRadius: "5px",
                                    padding: "8px 14px",
                                    cursor: "pointer",
                                    marginRight: "20px",
                                }}
                            >
                                {caseDiary?.bucketFileName ? "Regenerate Charge Sheet" : "Upload Case Diary"}
                            </button>

                            <button
                                onClick={handlePrint}
                                className='print-button'
                                style={{
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #ccc",
                                    borderRadius: "5px",
                                    padding: "8px 14px",
                                    cursor: "pointer",
                                    marginRight: "20px",
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    width="20"
                                    fill="currentColor"
                                    style={{ marginRight: "5px" }}
                                >
                                    <path d="M0 0h24v24H0z" fill="none" />
                                    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 
        1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"
                                    />
                                </svg>
                                Print
                            </button>
                        </div>
                    </div>

                </div>

                {viewCaseDiary && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h3>Case Diary</h3>
                            <div style={{ fontSize: '16px', alignContent: 'justify', alignItems: 'justify', textAlign: 'justify', maxHeight: '50vh', overflowY: 'auto', marginBottom: '16px' }}>
                                <p>{latestFir.caseDiary.text.split('\n').map((line, index) => (
                                    <span key={index}>
                                        {line}
                                        <br />
                                    </span>
                                ))}</p>
                            </div>
                            <div className="popup-buttons">
                                <button
                                    className="submit-popup-button"
                                    onClick={() => {
                                        setViewCaseDiary(false);
                                    }
                                    }>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {isLoading && (
                    <div className="popup-overlay">
                        <div className="loader-container">
                            <div className="loader"></div>
                        </div>
                    </div>
                )}
                {pendingMessage && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h3>Case Update</h3>
                            <div>
                                {pendingMessage}
                            </div>
                            <div className="popup-buttons">
                                <button
                                    className="submit-popup-button"
                                    onClick={() => {
                                        setPendingMessage(null);
                                    }
                                    }>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div className="popup-overlay">
                        <div className="popup-container">
                            <h3>Error</h3>
                            <div>
                                {errorMessage}
                            </div>
                            <div className="popup-buttons">
                                <button
                                    className="submit-popup-button"
                                    onClick={() => {
                                        setErrorMessage(null);
                                    }
                                    }>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default ChargeSheetView;
